/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * DashboardCard
 *
 * @file DashboardCard.jsx
 * @author Gladson Samuel S
 * @since 1.0.0
 */

import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { IconButton } from '@mui/material';
import {
  DashboardCardRoot,
  DashboardCardTitle,
  DashboardCardIconButtonMedium,
  DashboardCardIconMedium,
  DashboardCardHeader,
  DashboardCardActionsLeftContainer,
  DashboardCardActions,
  DashboardCardActionsRight,
  DashboardCardContent,
  DashboardCardIconOnlyButton,
  DashboardCardBadge,
  DashboardCardSubTitle,
  DashboardCardFooter,
  DashboardCardButton,
  DashboardCardTextIconButton
} from './styles/DashboardCardStyles';
import Icon from '../icons/Icon';
import DashboardCardIcon from './DashboardCardIcon';
import TooltipWrapper from '../tooltip/TooltipWrapper';
import FooterDeatilsCard from '../common/FooterDeatilsCard';
import SubscriptionStatusBtn from '../subscriptionStatusBtn/SubscriptionStatusBtn';

const isIconOnlyActionsGroup = (argActions) => (
  Array.isArray(argActions)
  && argActions.length > 0
  && argActions.every((action) => action.type !== 'custom' && action.iconName && !action.text)
);

const DashboardCard = (props) => {
  const {
    title,
    subTitle,
    footer,
    actions,
    children,
    config,
    onBackNavClick,
    handleTitleClick,
    footerDialogDetails,
    subscriptionText,
    onInit = () => {},
    classes: classesProp = {},
    components = {}
  } = props;
  const customCardContainerRef = useRef(null);
  const childContainerRef = useRef(null);

  const [isFooterDialogOpen, setFooterDialogOpen] = useState(false);

  const Root = components.Root || DashboardCardRoot;
  const ActionsLeftContainer = components.ActionsLeftContainer || DashboardCardActionsLeftContainer;
  const Title = components.Title || DashboardCardTitle;
  const Header = components.Header || DashboardCardHeader;
  const Content = components.Content || DashboardCardContent;
  const Actions = components.Actions || DashboardCardActions;
  const ActionsRight = components.ActionsRight || DashboardCardActionsRight;
  const ActionButton = components.Button || DashboardCardButton;
  const TextIconButton = components.TextIconButton || DashboardCardTextIconButton;

  useEffect(() => {
    onInit();
  }, []);

  useEffect(() => {
    const rootEl = customCardContainerRef.current;
    const childEl = childContainerRef.current;
    if (!rootEl || !childEl) return;

    const rootStyle = document.documentElement.style;

    let rootHeight = 0;
    let childHeight = 0;

    const calculateTitleHeight = () => {
      const topPosition = Math.max(0, rootHeight - childHeight);
      const value = `-${Math.floor(topPosition)}px`;
      const current = rootStyle.getPropertyValue('--slick-dots-position');

      if (current !== value) {
        rootStyle.setProperty('--slick-dots-position', value);
      }
    };

    rootHeight = rootEl.offsetHeight;
    childHeight = childEl.offsetHeight;

    calculateTitleHeight();

    const observer = new ResizeObserver((entries) => {
      requestAnimationFrame(() => {
        entries.forEach((entry) => {
          if (entry.target === rootEl) {
            rootHeight = entry.contentRect.height;
          }
          if (entry.target === childEl) {
            childHeight = entry.contentRect.height;
          }
        });

        calculateTitleHeight();
      });
    });

    observer.observe(rootEl);
    observer.observe(childEl);

    // eslint-disable-next-line consistent-return
    return () => {
      observer.unobserve(rootEl);
      observer.unobserve(childEl);
    };
  }, []);

  const openFooterDialog = () => {
    setFooterDialogOpen(true);
  };

  const closeFooterDialog = () => {
    setFooterDialogOpen(false);
  };

  const {
    left,
    right
  } = actions || {};

  const {
    showHeader = true,
    showBackNav = false,
    enableTitleClick = false,
    showCursorPointer = false,
    showSubscriptionStatus = false
  } = config || {};


  const renderTitle = () => {
    if (!title) {
      return null;
    }

    return (
      <Title
        className={classesProp.title}
        showCursorPointer={showCursorPointer}
        onClick={enableTitleClick ? handleTitleClick : undefined}
      >
        {title}
      </Title>
    );
  };

  const renderAction = (action) => {
    const ResolvedIcon = classesProp?.IconComponent || DashboardCardIcon;

    if (action.type === 'custom' && action.element) {
      return <div>{action.element}</div>;
    }
    if (!action.callback) {
      return null;
    }

    if (action.iconName && !action.text) {
      return (
        <TooltipWrapper tooltip={action.tooltip || ''} placement="top">
          <DashboardCardBadge
            badgeContent={action.badgeCount || 0}
            showZero={false}
            color="secondary"
            overlap="circular"
          >
            <DashboardCardIconOnlyButton
              hasBadge={action.badgeCount > 0}
              onClick={(event) => (action.disabled ? () => {} : action.callback(action, event))}
              disabled={action.disabled}
            >
              <ResolvedIcon name={action.iconName} className={classesProp.icon} />
            </DashboardCardIconOnlyButton>
          </DashboardCardBadge>
        </TooltipWrapper>
      );
    }

    if (action.iconName && action.text) {
      return (
        <TextIconButton
          className={components.TextIconButton ? undefined : classesProp.iconButton}
          onClick={() => action.callback(action)}
        >
          <span>
            <ResolvedIcon name={action.iconName} className={classesProp.icon} />
            {action.text}
          </span>
        </TextIconButton>
      );
    }

    return (
      <ActionButton
        className={components.Button ? `${action.className || ''}`.trim() : `${classesProp.button || ''} ${action.className || ''}`.trim()}
        onClick={() => action.callback(action)}
      >
        {action.text}
      </ActionButton>
    );
  };

  const renderActions = (argActions, className, alignLeft = false) => {
    if (!argActions) {
      return null;
    }
    const isCompact = isIconOnlyActionsGroup(argActions);

    const ActionContainer = alignLeft ? Actions : ActionsRight;

    return (
      <ActionContainer
        className={`${classesProp.actions || ''} ${className || ''}`.trim()}
        isCompact={isCompact}
        alignLeft={alignLeft}
      >
        {argActions.map((action) => renderAction(action))}
      </ActionContainer>
    );
  };

  const renderSubTitle = () => {
    if (!subTitle) {
      return null;
    }

    return (
      <DashboardCardSubTitle className={classesProp.subTitle}>
        {subTitle}
      </DashboardCardSubTitle>
    );
  };

  const renderFooter = () => {
    if (!footer) return null;
    const shouldShowFooterDialog =
    footerDialogDetails &&
    (footerDialogDetails.title || footerDialogDetails.content);

    return (
      <>
        <DashboardCardFooter>
          {footer}
          {shouldShowFooterDialog && (
          <IconButton
            aria-label="More information"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              openFooterDialog();
            }}
          >
            <Icon name="FooterInfoIcon" style={{ fontSize: 16 }} />
          </IconButton>
          )}
        </DashboardCardFooter>
        {shouldShowFooterDialog && (
        <FooterDeatilsCard
          open={isFooterDialogOpen}
          onClose={closeFooterDialog}
          title={footerDialogDetails.title}
          content={footerDialogDetails.content}
          confirmText={footerDialogDetails.confirmText || 'Confirm'}
          showCloseButton={footerDialogDetails.showCloseButton ?? true}
          onConfirm={footerDialogDetails.onConfirm || closeFooterDialog}
        />
        )}
      </>
    );
  };

  const renderSubscriptionStatusBtn = () => {
    if (!showSubscriptionStatus) {
      return null;
    }

    return (
      <SubscriptionStatusBtn
        text={subscriptionText}
      />
    );
  };

  return (
    <Root className={classesProp.root} ref={customCardContainerRef}>
      {showHeader ? (
        <Header className={classesProp.header}>
          <ActionsLeftContainer className={classesProp.actionsLeftContainer}>
            {showBackNav && (
              <DashboardCardIconButtonMedium onClick={onBackNavClick}>
                <DashboardCardIconMedium name="BackIcon" />
              </DashboardCardIconButtonMedium>
            )}
            {renderTitle()}
            {renderSubscriptionStatusBtn()}
            {renderActions(left, '', true)}
          </ActionsLeftContainer>
          {renderActions(right, classesProp.actionsRight, false)}
        </Header>
      ) : null}
      {renderSubTitle()}
      <Content className={classesProp.content} ref={childContainerRef}>
        {children}
      </Content>
      {renderFooter()}
    </Root>
  );
};

DashboardCard.propTypes = {
  title: PropTypes.string.isRequired,
  subTitle: PropTypes.string,
  footer: PropTypes.string,
  children: PropTypes.node.isRequired,
  actions: PropTypes.object,
  subscriptionText: PropTypes.string,
  config: PropTypes.object,
  onBackNavClick: PropTypes.func,
  handleTitleClick: PropTypes.func,
  onInit: PropTypes.func,
  footerDialogDetails: PropTypes.shape({
    title: PropTypes.string,
    content: PropTypes.string,
    confirmText: PropTypes.string,
    cancelText: PropTypes.string,
    showCloseButton: PropTypes.bool,
    onConfirm: PropTypes.func
  }),
  classes: PropTypes.shape({
    root: PropTypes.string,
    header: PropTypes.string,
    actions: PropTypes.string,
    actionsRight: PropTypes.string,
    content: PropTypes.string,
    title: PropTypes.string,
    icon: PropTypes.string,
    IconComponent: PropTypes.elementType,
    iconButton: PropTypes.string,
    button: PropTypes.string,
    actionsLeftContainer: PropTypes.string
  }),
  components: PropTypes.shape({
    Root: PropTypes.elementType,
    ActionsLeftContainer: PropTypes.elementType,
    Title: PropTypes.elementType,
    Header: PropTypes.elementType,
    Content: PropTypes.elementType,
    Actions: PropTypes.elementType,
    ActionsRight: PropTypes.elementType,
    Button: PropTypes.elementType,
    TextIconButton: PropTypes.elementType
  })
};

DashboardCard.defaultProps = {
  subTitle: '',
  footer: '',
  actions: {},
  subscriptionText: '',
  config: {
    showHeader: true,
    showBackNav: false,
    showSubscriptionStatus: false
  },
  onBackNavClick: () => {},
  handleTitleClick: () => {},
  onInit: () => {},
  footerDialogDetails: {
    title: '',
    content: '',
    confirmText: 'Confirm',
    cancelText: null,
    showCloseButton: false,
    onConfirm: () => {}
  },
  classes: {},
  components: {}
};


export default DashboardCard;
